import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Search from './components/search';
import Login from './components/login';
import FindADealer from './components/findADealer';
import MenuOverlay from './components/menuOverlay';
import NavigationItem from './components/navigationItem';
import { HeaderModel } from './models/header.model';
import { MenuContext, useMenuReducer } from './menuContext';
import Logo from './components/logo';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import MobileMenu from './components/mobileMenu';
import useStickyWrapper from '../../hooks/useStickyWrapper';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { documentService } from '../../utils/window.utils';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import HighLevelAlertMessage from './components/highLevelAlertMessage';
import useSitecoreContext from '../../hooks/useSitecoreContext';

const Header = (props): JSX.Element => {
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const isPreview = props.sitecoreContext.pageState === 'preview';
  const isSticky = !(isEEActive || isPreview);
  const context = useSitecoreContext();
  const isHighAlertMessage = context?.sitecoreContext?.severitySettings?.showSeverityMessage;

  const [navigationVisibility, setNavigationVisibility] = useState(false);

  useEffect(() => {
    // set height as css property so that content can be shifted below
    if (isSticky) {
      documentService().body.style.setProperty('--pfe-navigation--Height--actual', '85px');
    }
    setNavigationVisibility(true);
    const loadingDiv = documentService().getElementById('loading');
    if (loadingDiv) {
      loadingDiv.remove();
      const rootElement = documentService().getElementById('loading');
      if (rootElement) rootElement.style.display = 'none';
    }
    return (): void => {
      documentService().body.style.removeProperty('--pfe-navigation--Height--actual');
    };
  }, [isSticky]);

  const breakpointXL = useMediaQuery('(min-width: 1200px)', false);
  const breakpointLg = useMediaQuery('(min-width: 992px)');
  const model = new HeaderModel(props);

  const fields = model.fields;

  const [menuState, menuDispatcher] = useMenuReducer();

  const baseClassName = 'navigation';
  const wrapperClassName =
    baseClassName + (isEEActive ? ' ' + baseClassName + '--EE-active' : '') + (isPreview ? ' ' + baseClassName + '--preview' : '');

  // Component is unreleased
  if (props?.fields?.isComponentUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <MenuContext.Provider value={[menuState, menuDispatcher]}>
      {/* menu provider for main menu elements, not for mobile accordion*/}
      <div className={wrapperClassName} hidden={!navigationVisibility}>
        <MenuOverlay />
        <nav className="navigation__container">
          <Logo fields={fields} />
          {breakpointXL && (
            <ul className="navigation__item-wrapper navigation__item-wrapper--main">
              {model.navigationItemModelsArr.map((navigationItemModel) => (
                <NavigationItem key={navigationItemModel?.id} model={navigationItemModel} />
              ))}
            </ul>
          )}
          <ul className="navigation__item-wrapper navigation__item-wrapper--right">
            {breakpointLg && (
              <>
                {fields?.findDealer && <FindADealer fields={fields} />}
                {fields?.search && (
                  <Search sitecoreContext={props?.sitecoreContext} fields={fields} params={props.params} rendering={props.rendering} />
                )}
                {fields?.login && <Login {...props} sitecoreContext={props?.sitecoreContext} view={'desktop'} />}
              </>
            )}
            {!breakpointXL && (
              <MobileMenu model={model} fields={fields} sitecoreContext={props?.sitecoreContext} breakpointLg={breakpointLg} />
            )}
          </ul>
        </nav>
      </div>
      {isHighAlertMessage && <HighLevelAlertMessage/>}
    </MenuContext.Provider>
  );
};

const HeaderWrapper = (props): JSX.Element | null => {
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const isPreview = props?.sitecoreContext?.pageState === 'preview';
  const isEE = isEEActive || isPreview;

  const stickyWrapperEle = useStickyWrapper();

  if (isEE) return <Header {...props} />;

  if (!stickyWrapperEle) return null;

  return createPortal(<Header {...props} />, stickyWrapperEle);
};

export default withSitecoreContext()(HeaderWrapper);
